import * as React from "react";
import {
  Box,
  createStyles,
  Dialog,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import ReactPlayer from "react-player";

import { CookieConsentRequired } from "components";
import useTermlyConsent from "hooks/useTermlyConsent";
import theme from "theme";

import { AppContext } from "../../contexts";

const useStyles = makeStyles(() =>
  createStyles({
    responsiveWrapper: {
      position: "relative",
      paddingTop: "56.25%",
      "& .react-player": {
        position: "absolute",
        top: 0,
        left: 0,
      },
    },
    mediaThumbnail: {
      "& .react-player__play-icon": {
        opacity: 0.5,
        borderWidth: "13px 0px 13px 20px !important",
      },
      "&:hover .react-player__play-icon": {
        opacity: 1,
      },
      "& .react-player__preview": {
        pointerEvents: "none",
        borderRadius: 3,
      },
    },
    imageThumbnail: {
      borderRadius: 3,
      cursor: "default",
      objectFit: "contain",
    },
    videoBlockedPlaceholder: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundSize: "cover",
      backgroundPosition: "center",
      borderRadius: theme.shape.borderRadius,
      cursor: "pointer",
    },
    overlay: {
      backdropFilter: "blur(1px)",
      background: "rgba(0,0,0,0.55)",
      color: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      textAlign: "center",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
  }),
);

// https://stackoverflow.com/questions/2068344/how-do-i-get-a-youtube-video-thumbnail-from-the-youtube-api
type Resolution =
  | "default"
  | "mqdefault"
  | "hqdefault"
  | "sddefault"
  | "maxresdefault"; // NOTE: the `maxresdefault` version does not always exist, use a fallback if there's a 404
export const getThumbnailUrlFromMediaUrl = (
  mediaUrl: string,
  resolution: Resolution = "hqdefault",
) => {
  if (!mediaUrl) {
    return "";
  }
  // the regex below is from https://stackoverflow.com/a/51870158
  // the answer also provides an example with test urls: https://regexr.com/3u0d4
  const regExp = /(https?:\/\/)?(((m|www)\.)?(youtube(-nocookie)?|youtube.googleapis)\.com.*(v\/|v=|vi=|vi\/|e\/|embed\/|user\/.*\/u\/\d+\/)|youtu\.be\/)([_0-9a-z-]+)/im;
  const match = mediaUrl.match(regExp);
  const videoId = match && match[8] ? match[8] : false;
  return videoId ? `https://i.ytimg.com/vi/${videoId}/${resolution}.jpg` : "";
};

const StyledDialog = withStyles({
  container: {
    "& > div": {
      maxWidth: "750px",
    },
  },
})(Dialog);

const sizes = {
  small: {
    mobile: {
      width: "75px",
      height: "75px",
    },
    tablet: {
      width: "100px",
      height: "75px",
    },
    desktop: {
      width: "100px",
      height: "75px",
    },
  },
  responsive: {
    mobile: {
      width: "100%",
      height: "100%",
    },
    tablet: {
      width: "100%",
      height: "100%",
    },
    desktop: {
      width: "100%",
      height: "100%",
    },
  },
};

interface MediaPlayerProps {
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  size?: "small" | "responsive";
  playing?: boolean;
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({
  mediaUrl,
  thumbnailUrl,
  size = "responsive",
  playing = false,
}) => {
  const classes = useStyles();
  const { screenType } = React.useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const isVideoAllowed = useTermlyConsent(["performance"]);

  const width = sizes[size][screenType].width;
  const height = sizes[size][screenType].height;

  const renderConsentOverlay = () => {
    const effectiveThumbnailUrl =
      thumbnailUrl || getThumbnailUrlFromMediaUrl(mediaUrl || "");

    return (
      <Box
        className={classes.videoBlockedPlaceholder}
        style={{
          backgroundImage: effectiveThumbnailUrl
            ? `url(${effectiveThumbnailUrl})`
            : undefined,
          backgroundColor: effectiveThumbnailUrl ? undefined : "#000",
          width: "100%",
          height: "400px", // Fixed height for dialog
        }}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Box className={classes.overlay}>
          <CookieConsentRequired
            message={
              <>
                This video is blocked until you allow{" "}
                <strong>Performance</strong> cookies.
              </>
            }
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box style={{ width }}>
      {size === "responsive" ? (
        mediaUrl ? (
          !isVideoAllowed ? (
            renderConsentOverlay()
          ) : mediaUrl.includes(".mp4") ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              controlsList="nodownload"
              preload="auto"
              width="100%"
            >
              <source src={mediaUrl} type="video/mp4" />
              Your browser doesn&apos;t support embedded videos
            </video>
          ) : (
            <div className={classes.responsiveWrapper}>
              <ReactPlayer
                controls
                playing={playing}
                className="react-player"
                url={mediaUrl}
                width={width}
                height={height}
                config={{ youtube: { playerVars: { showinfo: 0, rel: 0 } } }}
              />
            </div>
          )
        ) : (
          <img
            className={classes.imageThumbnail}
            src={thumbnailUrl}
            style={{ width, maxHeight: height }}
            alt=""
          />
        )
      ) : (
        <>
          <StyledDialog
            fullWidth
            maxWidth="lg"
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          >
            {isVideoAllowed ? (
              <div className={classes.responsiveWrapper}>
                <ReactPlayer
                  controls
                  playing
                  className="react-player"
                  url={mediaUrl}
                  width="100%"
                  height="100%"
                  config={{ youtube: { playerVars: { showinfo: 0, rel: 0 } } }}
                />
              </div>
            ) : (
              renderConsentOverlay()
            )}
          </StyledDialog>
          {mediaUrl ? (
            <div
              className={classes.mediaThumbnail}
              onClick={() => setIsModalOpen(true)}
            >
              <ReactPlayer
                light={
                  thumbnailUrl || getThumbnailUrlFromMediaUrl(mediaUrl) || true
                }
                url={mediaUrl}
                width={width}
                height={height}
              />
            </div>
          ) : (
            thumbnailUrl && (
              <img
                className={classes.imageThumbnail}
                src={thumbnailUrl}
                style={{ width, maxHeight: height }}
                alt=""
              />
            )
          )}
        </>
      )}
    </Box>
  );
};

export default MediaPlayer;
